import { useState } from 'react'
import { toast } from 'sonner'
import { Check, ShieldCheck, TriangleAlert } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/shadcn-io/spinner'
import { cn } from '@/lib/utils'
import { Link } from 'react-router-dom'
import { useAgentSettings, type AgentSettings, type ListingOnGenerate } from '@/lib/agentSettings'
import { AI_TERMS_VERSION } from '@/components/listings/ai/aiTermsContent'

/**
 * The agent's own choices, saved as they are changed.
 *
 * Owner validation is the one that decides how far a listing can go by
 * itself: a listing the owner must confirm always goes through approval, so
 * "publish straight away" is only on offer while it is off.
 */
const ON_GENERATE: { value: ListingOnGenerate; title: string; description: string }[] = [
    { value: 'draft', title: 'Keep it as a draft', description: 'The listing lands in your Draft tab. You send it on yourself when you are ready.' },
    { value: 'publish', title: 'Put it live', description: 'Skips the draft. The listing goes straight to the site.' },
]

export default function SettingsPage() {
    const { settings, loaded, save } = useAgentSettings()
    const [saving, setSaving] = useState<string | null>(null)
    const needsOwner = Boolean(settings.requireOwnerValidation)
    const termsOutdated = Boolean(settings.aiTermsAcceptedAt) && settings.aiTermsVersion !== AI_TERMS_VERSION

    const change = async (patch: Partial<AgentSettings>, what: string) => {
        setSaving(what)
        try {
            await save(patch)
            toast.success('Saved.')
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Your settings could not be saved.')
        } finally {
            setSaving(null)
        }
    }

    return (
        <div className='mx-auto w-full max-w-3xl pb-16'>
            <h5 className='font-bold h5 uppercase'>Settings</h5>
            <p className='mt-1 text-sm text-muted-foreground'>Your own preferences. They apply to every listing you create.</p>

            <section className='mt-6'>
                <h6 className='text-sm font-semibold'>Owner validation</h6>
                <div className='mt-1 flex items-start justify-between gap-4 border-b py-4'>
                    <div className='min-w-0'>
                        <p className='text-sm font-medium'>Ask the owner to confirm the listing</p>
                        <p className='mt-0.5 text-sm text-muted-foreground'>
                            The owner is sent the listing to confirm. A listing that needs confirming always goes through approval before it can appear on
                            the site.
                        </p>
                    </div>
                    <div className='flex items-center gap-2'>
                        {saving === 'requireOwnerValidation' && <Spinner variant='circle' className='h-4 w-4' />}
                        <Switch
                            checked={needsOwner}
                            disabled={!loaded || saving !== null}
                            onCheckedChange={(on) => change({ requireOwnerValidation: on ? 1 : 0 }, 'requireOwnerValidation')}
                        />
                    </div>
                </div>
            </section>

            <section className='mt-8'>
                <h6 className='text-sm font-semibold'>When a listing is generated with AI</h6>
                <p className='mt-0.5 text-sm text-muted-foreground'>What happens the moment you press Generate listing in the review.</p>
                {needsOwner ? (
                    <p className='mt-3 flex items-start gap-2 rounded-lg border bg-gray-50 p-3 text-sm text-gray-700'>
                        <ShieldCheck className='mt-0.5 h-4 w-4 shrink-0' />
                        The owner has to confirm your listings, so a generated listing goes for approval. Turn owner validation off to publish straight
                        from the review.
                    </p>
                ) : (
                    <div className='mt-3 grid gap-3 sm:grid-cols-2'>
                        {ON_GENERATE.map((option) => {
                            const chosen = settings.listingOnGenerate === option.value
                            return (
                                <Card
                                    key={option.value}
                                    role='button'
                                    tabIndex={0}
                                    aria-pressed={chosen}
                                    onClick={() => !saving && change({ listingOnGenerate: option.value }, 'listingOnGenerate')}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            change({ listingOnGenerate: option.value }, 'listingOnGenerate')
                                        }
                                    }}
                                    className={cn('cursor-pointer gap-0 p-4 transition-shadow hover:shadow-sm', chosen && 'border-black ring-1 ring-black')}>
                                    <CardContent className='p-0'>
                                        <div className='flex items-center justify-between gap-2'>
                                            <p className='text-sm font-medium'>{option.title}</p>
                                            {chosen && <Check className='h-4 w-4 shrink-0' />}
                                        </div>
                                        <p className='mt-1 text-sm text-muted-foreground'>{option.description}</p>
                                    </CardContent>
                                </Card>
                            )
                        })}
                    </div>
                )}
                {!needsOwner && settings.listingOnGenerate === 'publish' && (
                    <p className='mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
                        <TriangleAlert className='mt-0.5 h-4 w-4 shrink-0' />
                        Published listings are on the site straight away, with nobody else checking them. What the AI read is only as right as your
                        documents, so look over the review before you generate.
                    </p>
                )}
            </section>

            <section className='mt-8'>
                <h6 className='text-sm font-semibold'>Location</h6>
                <div className='flex items-start justify-between gap-4 border-b py-4'>
                    <div className='min-w-0'>
                        <p className='text-sm font-medium'>Area circle</p>
                        <p className='mt-0.5 text-sm text-muted-foreground'>
                            How large the approximate-location circle is on a new listing, in metres. The exact address is never shown to buyers.
                        </p>
                    </div>
                    <div className='flex items-center gap-2'>
                        {saving === 'defaultRadius' && <Spinner variant='circle' className='h-4 w-4' />}
                        <input
                            type='number'
                            min={50}
                            max={5000}
                            step={50}
                            defaultValue={settings.defaultRadius}
                            key={settings.defaultRadius}
                            disabled={!loaded || saving !== null}
                            onBlur={(e) => {
                                const next = Number(e.target.value)
                                if (next !== settings.defaultRadius) change({ defaultRadius: next }, 'defaultRadius')
                            }}
                            className='h-9 w-28 rounded-md border border-gray-200 bg-white px-2.5 text-sm outline-none focus:border-gray-500'
                        />
                        <span className='text-sm text-muted-foreground'>m</span>
                    </div>
                </div>
            </section>

            <section className='mt-8'>
                <h6 className='text-sm font-semibold'>Agreements</h6>
                <div className='mt-1 flex flex-wrap items-center justify-between gap-3 border-b py-4'>
                    <div className='min-w-0'>
                        <p className='text-sm font-medium'>Xpillar Intelligence — Terms of Use</p>
                        <p className='mt-0.5 text-sm text-muted-foreground'>
                            {termsOutdated
                                ? 'These terms have been revised since you accepted them.'
                                : settings.aiTermsAcceptedAt
                                  ? `Accepted on ${new Date(settings.aiTermsAcceptedAt).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}${settings.aiTermsVersion ? ` · version ${settings.aiTermsVersion}` : ''}.`
                                  : 'Not accepted yet. You are asked to accept them the first time you use the AI.'}
                        </p>
                    </div>
                    <Button size='sm' variant='outline' asChild>
                        <Link to='/settings/ai-terms'>{settings.aiTermsAcceptedAt && !termsOutdated ? 'Read the terms' : 'Read and accept'}</Link>
                    </Button>
                </div>
            </section>
        </div>
    )
}
